import { toast } from 'react-toastify';
import {
  addOrUpdatePortForwardingConfig,
  AddPortForwardingConfigPayload,
  getGatewayDeviceId,
} from '../../Services/serviceURL';
import { PortFormData } from './remoteAccessUtils';

type SaveResponse = Awaited<ReturnType<typeof addOrUpdatePortForwardingConfig>>;

function parseApiErrorMessage(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as { error?: string };
    return parsed.error || raw;
  } catch {
    return raw;
  }
}

function showSaveWarning(errorMessage: string): void {
  if (errorMessage.includes('Field Validation Error:')) {
    toast.warning(errorMessage);
    return;
  }
  toast.warning(`Configuration saved with warning: ${errorMessage}`);
}

function handleSuccessResponse(
  response: SaveResponse,
  onCloseModal: () => void,
  refreshConfigs: () => Promise<void>
): void {
  let hasWarning = false;

  if (response.Data?.success === false && response.Data?.errorMessage) {
    const errorMessage = parseApiErrorMessage(response.Data.errorMessage);
    console.warn(' Port forwarding config saved but with warnings:', errorMessage);
    showSaveWarning(errorMessage);
    hasWarning = true;
  }

  if (!hasWarning) {
    toast.success('Remote access requested successfully');
    onCloseModal();
  }

  void refreshConfigs();
}

async function handleAxiosSaveMessage(
  errorMessage: string,
  refreshConfigs: () => Promise<void>
): Promise<void> {
  if (errorMessage.includes('Field Validation Error:')) {
    console.warn('Port forwarding config saved but with warnings:', errorMessage);
    toast.warning(errorMessage);
    await refreshConfigs();
    return;
  }

  toast.error(`Failed to save port forwarding configuration: ${errorMessage}`);
}

function handleSaveError(error: unknown, refreshConfigs: () => Promise<void>): void {
  const axiosError = error as {
    response?: { data?: { Message?: string; message?: string }; status?: number; statusText?: string };
  };

  const primaryMessage = axiosError.response?.data?.Message;
  if (primaryMessage) {
    void handleAxiosSaveMessage(primaryMessage, refreshConfigs);
    return;
  }

  const altMessage = axiosError.response?.data?.message;
  if (altMessage) {
    void handleAxiosSaveMessage(altMessage, refreshConfigs);
    return;
  }

  if (axiosError.response?.status) {
    const errorMsg = `Failed to save port forwarding configuration: HTTP ${axiosError.response.status} - ${axiosError.response.statusText || 'Unknown error'}`;
    toast.error(errorMsg);
    return;
  }

  toast.error('Failed to save port forwarding configuration. Please try again.');
}

export async function savePortForwardingConfiguration(params: {
  assetRecordId: string;
  form: PortFormData;
  onCloseModal: () => void;
  refreshConfigs: () => Promise<void>;
}): Promise<{ success: boolean }> {
  const { assetRecordId, form, onCloseModal, refreshConfigs } = params;

  const gatewayDeviceId = await getGatewayDeviceId(assetRecordId);
  if (!gatewayDeviceId) {
    toast.error('Gateway Device ID not found for this asset');
    return { success: false };
  }

  const accessLimitInMinutes = form.accessLimit ? parseInt(form.accessLimit, 10) : 60;
  const configPayload: AddPortForwardingConfigPayload = {
    id: '',
    serviceName: form.serviceName.trim() || 'Remote Access',
    inputPort: parseInt(form.inputPort, 10),
    targetPort: parseInt(form.targetPort, 10),
    targetIpAddress: form.targetIpAddress.trim(),
    protocol: form.protocol.toUpperCase(),
    accessLimit: accessLimitInMinutes * 60,
    tls: form.tls,
    deviceId: gatewayDeviceId,
    currentUserIpAddress: '',
  };

  const response = await addOrUpdatePortForwardingConfig(configPayload);

  if (response?.Success === false) {
    const errorMessage = response.Message || response.ErrorMessage || 'Operation failed';
    console.error(' Port forwarding config save failed:', errorMessage);
    toast.error(`Failed to save port forwarding configuration: ${errorMessage}`);
    return { success: false };
  }

  if (response?.Success === true) {
    handleSuccessResponse(response, onCloseModal, refreshConfigs);
    return { success: true };
  }

  return { success: false };
}

export function handlePortForwardingSaveError(error: unknown, refreshConfigs: () => Promise<void>): void {
  console.error(' Error saving port forwarding config:', error);
  handleSaveError(error, refreshConfigs);
}
